import React, { useState, useEffect } from 'react';
import { Button } from './Button';

const ServiceWorkerButton: React.FC = () => {
  const [status, setStatus] = useState<string>('Checking Service Worker...');
  const [swStatus, setSwStatus] = useState<{
    registered: boolean;
    message: string;
    state?: string;
  }>({ registered: false, message: '' });

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      setStatus('Not supported');
      setSwStatus({ registered: false, message: 'Not supported' });
      return;
    }

    const checkSW = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          setSwStatus({
            registered: true,
            message: `Registered: ${registration.scope}`,
            state: 'registered',
          });
          setStatus('Registered');

          // Listen for SW state changes
          registration.addEventListener('updatefound', () => {
            const installingWorker = registration.installing;
            if (installingWorker) {
              installingWorker.addEventListener('statechange', () => {
                setSwStatus((prev) => ({
                  ...prev,
                  state: installingWorker.state,
                  message: `State: ${installingWorker.state}`,
                }));
                setStatus(`Service Worker ${installingWorker.state}`);
              });
            }
          });
        } else {
          setSwStatus({ registered: false, message: 'Not registered' });
          setStatus('Not registered');
        }
      } catch (error) {
        setSwStatus({ registered: false, message: 'Failed to check' });
        setStatus('Failed');
      }
    };

    checkSW();

    // Listen for controller changes (e.g., SW takes control)
    const handleControllerChange = () => {
      setStatus('Updated, reloading...');
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  const registerSW = async () => {
    if (!('serviceWorker' in navigator)) {
      setStatus('Not supported');
      return;
    }
    try {
      const registration = await navigator.serviceWorker.register(new URL('../../../sw.js', import.meta.url), { scope: '/' });
      setSwStatus({
        registered: true,
        message: `Registered: ${registration.scope}`,
        state: 'registering',
      });
      setStatus('Registered!');
      location.reload();
    } catch (error) {
      setSwStatus({ registered: false, message: 'Failed to register' });
      setStatus('Failed to register');
    }
  };

  const unregisterSW = async () => {
    if (!('serviceWorker' in navigator)) {
      setStatus('Not supported');
      return;
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.unregister();
        setSwStatus({ registered: false, message: 'Unregistered' });
        setStatus('Unregistered');
      }
    } catch (error) {
      setSwStatus({ registered: false, message: 'Failed to unregister Service Worker' });
      setStatus('Failed to unregister Service Worker');
    }
  };

  return (
    <div>
      <p>Status: {status}</p>
      <Button onClick={swStatus.registered ? unregisterSW : registerSW} fullWidth>
        {swStatus.registered ? 'Unregister' : 'Register'}
      </Button>
    </div>
  );
};

export default ServiceWorkerButton;
